/**
 * EU online withdrawal function (Directive 2011/83/EU Art 11a). Wire types for the
 * `withdrawal.getContext` / `withdrawal.submit` backend actions.
 */
export interface WithdrawalContractView {
    id: string;
    planName?: string;
    status: string;
    /** False only when the status proves no charge was made. */
    charged: boolean;
}

export interface WithdrawalContext {
    enabled: boolean;
    name: string;
    email: string;
    contracts: WithdrawalContractView[];
    supportEmail: string;
    /** Only the workspace owner may submit; other members see ownerEmail. */
    canWithdraw: boolean;
    ownerEmail?: string;
}

export type WithdrawalContractOutcome = 'cancelled' | 'already_ending' | 'cancel_failed';

export interface WithdrawalSubmitInput {
    name: string;
    email: string;
    subscriptionIds: string[];
}

export interface WithdrawalResult {
    success: true;
    statementId: string;
    submittedAt: string;
    contracts: Array<WithdrawalContractView & { outcome: WithdrawalContractOutcome }>;
    cancelledAll: boolean;
    anyCharged: boolean;
    receiptQueued: boolean;
    supportEmail: string;
    /** True when the contract already had a statement: the stored record, no second receipt. */
    duplicate?: boolean;
}

const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

export type WithdrawalFormError = 'name' | 'email' | 'contracts';

/** First problem with the form, or null. Mirrors the server's validation. */
export function validateWithdrawalForm(input: WithdrawalSubmitInput): WithdrawalFormError | null {
    if (!input.name.trim() || input.name.trim().length > 200) return 'name';
    const email = input.email.trim();
    if (!email || email.length > 254 || !EMAIL_RE.test(email)) return 'email';
    if (input.subscriptionIds.length === 0) return 'contracts';
    return null;
}

/** The section shows only when the server says it is on AND there is a contract to withdraw from. */
export function shouldShowWithdrawal(ctx: WithdrawalContext | undefined): ctx is WithdrawalContext {
    return !!ctx && ctx.enabled && ctx.contracts.length > 0;
}
